import { ReactNode, useId } from 'react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ValidatedInputProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  type?: 'text' | 'email' | 'password';
  error?: boolean;
  errorMessage?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  className?: string;
  /** For password managers: "email", "current-password", "new-password"… */
  autoComplete?: string;
}

export const ValidatedInput = ({
  label,
  placeholder,
  value,
  onChange,
  onKeyDown,
  type = 'text',
  error = false,
  errorMessage,
  startIcon,
  endIcon,
  className,
  autoComplete,
}: ValidatedInputProps) => {
  const inputId = useId();

  return (
    <div className={className}>
      <Label htmlFor={inputId} className="mb-2 block text-sm font-medium">{label}</Label>

      <div className="relative">
        {startIcon && (
          <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground [&_svg]:size-4">
            {startIcon}
          </span>
        )}

        <Input
          id={inputId}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          autoComplete={autoComplete}
          aria-invalid={error || undefined}
          className={cn('h-11', startIcon && 'ps-9', endIcon && 'pe-9')}
        />

        {endIcon && (
          <span className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground [&_svg]:size-4">
            {endIcon}
          </span>
        )}
      </div>

      {error && errorMessage && (
        <p className="mt-1 ms-0.5 animate-in text-xs text-destructive fade-in slide-in-from-top-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
